import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { jsPDF } from "jspdf";

import { Button, Input, ServerTablePagination } from "../components/ui";
import { ownerSelector } from "../store/store";
import {
  downloadDailyReportHistory,
  fetchDailyReportHistory,
  fetchDailyReportPage,
  previewDailyReportEmail,
  saveDailyReportPreferences,
  type DailyReportExportItem,
  type DailyReportHistoryItem,
  type DailyReportPageData,
} from "../utils";
import { EMAIL_CHANNEL_REQUIRED_MESSAGE } from "../utils/sequenceTemplate";

const HISTORY_PAGE_SIZE = 10;

const fieldLabelClass = "mb-1.5 text-[11px] font-semibold text-dark-blue";
const fieldInputClass = "rounded-[7px] border-line text-sm text-dark-blue";

const formatReportDate = (value: string | Date | null | undefined) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(date);
};

const formatReportDateShort = (value: string | Date | null | undefined) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
};

const formatSentTime = (value: string | Date | null | undefined, deliveryTimeLabel: string) => {
  if (!value) return `Scheduled for ${deliveryTimeLabel}`;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return `Scheduled for ${deliveryTimeLabel}`;
  const dayLabel = formatReportDate(date);
  const timeLabel = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
  return `${dayLabel} · Sent by email at ${timeLabel}`;
};

const exportReportsPdf = (items: DailyReportExportItem[], filename: string) => {
  const pdf = new jsPDF({ unit: "pt", format: "a4" });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const marginLeft = 40;
  const marginTop = 48;
  const lineHeight = 18;
  const colWidths = [110, 120, 90, 90, 90];
  const columns = [
    "Report date",
    "Connection requests",
    "Replies",
    "Interested",
    "Status",
  ];

  pdf.setFillColor(245, 247, 250);
  pdf.rect(0, 0, pageWidth, 50, "F");
  pdf.setFontSize(18);
  pdf.setTextColor(17, 15, 42);
  pdf.text("Daily email reports", marginLeft, 30);

  let x = marginLeft;
  let y = marginTop;

  pdf.setFontSize(9);
  pdf.setTextColor(90, 93, 110);
  pdf.setFont("helvetica", "bold");
  columns.forEach((col, index) => {
    pdf.text(col, x, y);
    x += colWidths[index];
  });

  y += 18;
  pdf.setFont("helvetica", "normal");
  pdf.setTextColor(30, 30, 30);

  items.forEach((item) => {
    if (y > 760) {
      pdf.addPage();
      y = 48;
    }

    const cells = [
      formatReportDateShort(item.date),
      String(item.connectionRequestsSent ?? 0),
      String(item.repliesReceived ?? 0),
      String(item.interestedProspects ?? 0),
      item.summary || "Email sent",
    ];

    x = marginLeft;
    cells.forEach((cell, index) => {
      pdf.text(String(cell || "—"), x, y, { maxWidth: colWidths[index] - 8 });
      x += colWidths[index];
    });

    y += lineHeight;
  });

  pdf.save(filename.endsWith(".pdf") ? filename : "daily-email-reports.pdf");
};

export default function DailyReportsPage() {
  const { owner, currentWorkspace, currentUser } = useSelector(ownerSelector);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [pageData, setPageData] = useState<DailyReportPageData | null>(null);
  const [historyItems, setHistoryItems] = useState<DailyReportHistoryItem[]>([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(0);
  const [recipientsText, setRecipientsText] = useState("");
  const [deliveryTimeLabel, setDeliveryTimeLabel] = useState("08:00 AM · UTC");
  const [subject, setSubject] = useState("Your InnLead daily outreach summary");

  const workspaceId = useMemo(() => {
    if (typeof currentWorkspace !== "string") return currentWorkspace?._id as string | undefined;
    try {
      return JSON.parse(currentWorkspace || "null")?._id as string | undefined;
    } catch {
      return undefined;
    }
  }, [currentWorkspace]);

  const userId = currentUser?.userId as string | undefined;
  const ownerId = owner?._id as string | undefined;
  const emailVerified = Boolean(pageData?.emailChannel?.verified);
  const canSendReports = Boolean(pageData?.canSendReports);

  const loadHistory = useCallback(
    async (pageIndex: number) => {
      if (!ownerId && !userId && !workspaceId) {
        setHistoryItems([]);
        setHistoryTotal(0);
        setHistoryLoading(false);
        return;
      }

      setHistoryLoading(true);
      try {
        const data = await fetchDailyReportHistory({
          ownerId,
          userId,
          workspaceId,
          page: pageIndex + 1,
          limit: HISTORY_PAGE_SIZE,
        });
        setHistoryItems(data.items || []);
        setHistoryTotal(Number(data.total || 0));
      } catch (error) {
        setHistoryItems([]);
        setHistoryTotal(0);
        toast.error(error instanceof Error ? error.message : "Unable to load previous reports");
      } finally {
        setHistoryLoading(false);
      }
    },
    [ownerId, userId, workspaceId]
  );

  useEffect(() => {
    let active = true;

    const load = async () => {
      if (!ownerId && !userId && !workspaceId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const data = await fetchDailyReportPage({ ownerId, userId, workspaceId });
        if (!active) return;
        setPageData(data);
        setRecipientsText(data.preferences.recipientsText || "");
        setDeliveryTimeLabel(data.preferences.deliveryTimeLabel || "08:00 AM · UTC");
        setSubject(data.preferences.subject || "Your InnLead daily outreach summary");
      } catch (error) {
        if (active) {
          setPageData(null);
          toast.error(error instanceof Error ? error.message : "Unable to load daily reports");
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    void load();
    return () => {
      active = false;
    };
  }, [ownerId, userId, workspaceId]);

  useEffect(() => {
    void loadHistory(historyPage);
  }, [historyPage, loadHistory]);

  const summaryStats = [
    {
      value: String(pageData?.summary.connectionRequestsSent ?? 0),
      label: "Connection requests sent",
    },
    {
      value: String(pageData?.summary.repliesReceived ?? 0),
      label: "Replies received",
    },
    {
      value: String(pageData?.summary.interestedProspects ?? 0),
      label: "Interested prospects",
    },
  ];

  const handleSave = async () => {
    if (!recipientsText.trim()) {
      toast.error("Add at least one recipient email.");
      return;
    }

    setSaving(true);
    try {
      const result = await saveDailyReportPreferences({
        ownerId,
        userId,
        workspaceId,
        recipientsText,
        deliveryTimeLabel,
        subject,
      });
      setPageData((current) =>
        current ? { ...current, preferences: result.preferences } : current
      );
      toast.success("Email preferences saved.");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Unable to save preferences");
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    if (!canSendReports) {
      toast.info(EMAIL_CHANNEL_REQUIRED_MESSAGE);
      return;
    }

    setPreviewing(true);
    try {
      const result = await previewDailyReportEmail({ ownerId, userId, workspaceId });
      toast.success(result.message || "Preview email sent.");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Unable to send preview email");
    } finally {
      setPreviewing(false);
    }
  };

  const handleDownloadAll = async () => {
    setDownloadingAll(true);
    try {
      const result = await downloadDailyReportHistory({ ownerId, userId, workspaceId });
      exportReportsPdf(result.items || [], result.filename || "daily-email-reports.pdf");
      toast.success(`Exported ${result.count} report${result.count === 1 ? "" : "s"} as PDF.`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Unable to export reports");
    } finally {
      setDownloadingAll(false);
    }
  };

  const summarySubtitle = formatSentTime(
    pageData?.lastSentAt,
    pageData?.preferences.deliveryTimeLabel || deliveryTimeLabel
  );

  return (
    <section className="h-full overflow-y-auto bg-paper px-4 py-6 sm:px-9 sm:py-9">
      <div className="mx-auto max-w-[1600px]">
        <header className="mb-[22px]">
          <h1 className="text-xl font-semibold tracking-[-0.02em] text-dark-blue sm:text-[27px]">
            Daily email reports
          </h1>
          <p className="mt-1 text-sm text-muted">
            Send a daily sales-outreach summary to your team by email.
          </p>
        </header>

        {!loading && !emailVerified ? (
          <div className="mb-4 rounded-[10px] border border-notice-border bg-notice-bg px-4 py-3 text-sm text-dark-blue">
            <p className="font-semibold text-notice-text">Connect your email channel first</p>
            <p className="mt-1 text-notice-text">
              Daily reports are sent through your verified SMTP or SendGrid integration.{" "}
              {EMAIL_CHANNEL_REQUIRED_MESSAGE}
            </p>
            <Link
              to="/integrations"
              className="mt-3 inline-flex text-xs font-semibold text-mini-avatar-fg hover:underline"
            >
              Open Integrations →
            </Link>
          </div>
        ) : null}

        <div className="grid gap-4 lg:grid-cols-2">
          <div className="rounded-[10px] border border-line bg-white p-[19px]">
            <span className="inline-flex rounded-xl bg-mini-avatar px-[7px] py-1 text-[10px] font-semibold text-mini-avatar-fg">
              EMAIL REPORT
            </span>
            <h2 className="mt-0 text-[15px] font-semibold tracking-[-0.025em] text-dark-blue">
              Today&apos;s email summary
            </h2>
            <p className="mt-1 text-sm text-muted">
              {loading
                ? "Loading report summary..."
                : summarySubtitle || `Scheduled for ${deliveryTimeLabel}`}
            </p>

            <div className="my-[19px] grid grid-cols-[auto_1fr] items-center gap-x-[14px] gap-y-[10px]">
              {summaryStats.map((stat) => (
                <Fragment key={stat.label}>
                  <b className="text-[20px] font-semibold text-dark-blue">{stat.value}</b>
                  <span className="text-xs text-muted">{stat.label}</span>
                </Fragment>
              ))}
            </div>

            <Button
              variant="primary"
              size="sm"
              className="mt-4 font-semibold"
              isLoading={previewing}
              isDisabled={!canSendReports || loading}
              onClick={handlePreview}
            >
              Preview email
            </Button>
          </div>

          <div className="rounded-[10px] border border-line bg-white p-[19px]">
            <h2 className="text-[15px] font-semibold tracking-[-0.025em] text-dark-blue">
              Email delivery settings
            </h2>
            <p className="mt-1 text-sm text-muted">
              Choose who receives the daily email report.
            </p>

            <Input
              label="Email recipients"
              value={recipientsText}
              onChange={(event) => setRecipientsText(event.target.value)}
              labelClassName={fieldLabelClass}
              className={fieldInputClass}
              size="sm"
              containerClassName="mt-4"
              disabled={loading}
            />
            <Input
              label="Delivery time"
              value={deliveryTimeLabel}
              onChange={(event) => setDeliveryTimeLabel(event.target.value)}
              labelClassName={fieldLabelClass}
              className={fieldInputClass}
              size="sm"
              containerClassName="mt-4"
              disabled={loading}
              placeholder="08:00 AM · Asia/Karachi"
            />
            <Input
              label="Email subject"
              value={subject}
              onChange={(event) => setSubject(event.target.value)}
              labelClassName={fieldLabelClass}
              className={fieldInputClass}
              size="sm"
              containerClassName="mt-4"
              disabled={loading}
            />

            <Button
              variant="outline"
              size="sm"
              className="mt-4 border-line bg-white font-semibold text-dark-blue hover:bg-paper"
              isLoading={saving}
              isDisabled={loading}
              onClick={handleSave}
            >
              Save email preferences
            </Button>
          </div>
        </div>

        <div className="mt-4 rounded-[10px] border border-line bg-white p-[19px]">
          <div className="mb-[17px] flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
            <h2 className="text-[15px] font-semibold tracking-[-0.025em] text-dark-blue">
              Previous email reports
            </h2>
            <button
              type="button"
              className="w-fit text-xs font-semibold text-mini-avatar-fg hover:underline disabled:cursor-not-allowed disabled:opacity-50"
              disabled={!historyTotal || downloadingAll}
              onClick={() => void handleDownloadAll()}
            >
              {downloadingAll ? "Exporting PDF..." : "Export PDF →"}
            </button>
          </div>

          {historyLoading ? (
            <p className="py-3 text-sm text-muted">Loading previous reports...</p>
          ) : historyItems.length ? (
            <>
              <div className="hidden border-b border-thread-line pb-2 text-[10px] font-semibold uppercase tracking-[0.08em] text-muted sm:grid sm:grid-cols-[1.4fr_repeat(3,0.7fr)_1fr] sm:gap-3">
                <span>Report date</span>
                <span className="text-right">Connection requests</span>
                <span className="text-right">Replies</span>
                <span className="text-right">Interested</span>
                <span>Status</span>
              </div>

              {historyItems.map((report, index) => (
                <div
                  key={report._id}
                  className={`py-[13px] text-xs ${
                    index > 0 ? "border-t border-thread-line" : ""
                  }`}
                >
                  <b className="block font-semibold text-dark-blue sm:hidden">
                    {formatReportDate(report.date) || report.date}
                  </b>
                  <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-2 sm:hidden">
                    <div>
                      <dt className="text-[10px] text-muted">Connection requests</dt>
                      <dd className="font-semibold text-dark-blue">
                        {report.connectionRequestsSent ?? 0}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-[10px] text-muted">Replies</dt>
                      <dd className="font-semibold text-dark-blue">
                        {report.repliesReceived ?? 0}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-[10px] text-muted">Interested</dt>
                      <dd className="font-semibold text-dark-blue">
                        {report.interestedProspects ?? 0}
                      </dd>
                    </div>
                    <div className="col-span-2">
                      <dt className="text-[10px] text-muted">Status</dt>
                      <dd className="text-[11px] text-muted">{report.summary}</dd>
                    </div>
                  </dl>
                  <div className="mt-2 hidden sm:grid sm:grid-cols-[1.4fr_repeat(3,0.7fr)_1fr] sm:items-center sm:gap-3">
                    <span className="font-semibold text-dark-blue">
                      {formatReportDate(report.date) || report.date}
                    </span>
                    <span className="text-right font-semibold text-dark-blue sm:text-sm">
                      {report.connectionRequestsSent ?? 0}
                    </span>
                    <span className="text-right font-semibold text-dark-blue sm:text-sm">
                      {report.repliesReceived ?? 0}
                    </span>
                    <span className="text-right font-semibold text-dark-blue sm:text-sm">
                      {report.interestedProspects ?? 0}
                    </span>
                    <span className="text-[11px] text-muted">{report.summary}</span>
                  </div>
                </div>
              ))}

              {historyTotal > HISTORY_PAGE_SIZE ? (
                <ServerTablePagination
                  page={historyPage}
                  pageSize={HISTORY_PAGE_SIZE}
                  total={historyTotal}
                  rowsPerPageOptions={[HISTORY_PAGE_SIZE]}
                  onPageChange={setHistoryPage}
                  onPageSizeChange={() => undefined}
                  loading={historyLoading}
                  className="mt-2 border-t border-thread-line px-0"
                />
              ) : null}
            </>
          ) : (
            <p className="py-3 text-sm text-muted">
              {emailVerified
                ? "No daily reports have been sent yet."
                : "Connect and verify your email channel to start receiving daily reports."}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
